import React, { useEffect, useState, useRef } from 'react';
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import metadata from 'libphonenumber-js/metadata.full.json';

function PhoneNumberInput({
  selectedCountry,
  localNumber,
  onChangeCountry,
  onChangeNumber,
  onPhoneNumberChange,
}) {
  const [countryList, setCountryList] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const selectedItemRef = useRef(null);

  useEffect(() => {
    const countries = getCountries().map(code => ({
      code,
      name: new Intl.DisplayNames(['en'], { type: 'region' }).of(code),
      dial_code: `+${getCountryCallingCode(code, metadata)}`,
    }));

    countries.sort((a, b) => a.name.localeCompare(b.name));
    setCountryList(countries);
  }, []);

  const selected = countryList.find(c => c.code === selectedCountry);

  const filteredList = countryList.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (code) => {
    onChangeCountry(code);
    setDropdownOpen(false);
    setSearchTerm('');
  };

  useEffect(() => {
    if (selectedCountry && localNumber) {
      const fullNumber = parsePhoneNumberFromString(localNumber, selectedCountry, metadata);
      if (fullNumber) {
        const rawNumber = `${fullNumber.countryCallingCode}${fullNumber.nationalNumber}`;
        onPhoneNumberChange(rawNumber);
      } else {
        onPhoneNumberChange('');
      }
    } else {
      onPhoneNumberChange('');
    }
  }, [selectedCountry, localNumber]);

  useEffect(() => {
    if (dropdownOpen && selectedItemRef.current && dropdownRef.current) {
      const dropdown = dropdownRef.current;
      const selectedItem = selectedItemRef.current;
      dropdown.scrollTop = selectedItem.offsetTop - dropdown.clientHeight / 2 + selectedItem.clientHeight / 2;
    }
  }, [dropdownOpen]);

  return (
    <div className="flex">
      {/* Country dropdown */}
      <div className="relative w-32">
        <button
          type="button"
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="border p-2 rounded-l w-full bg-white text-left"
        >
          {selected ? `${selected.code} (${selected.dial_code})` : 'Select Country'}
        </button>
        {dropdownOpen && (
          <div className="absolute z-10 bg-white border w-60 shadow rounded">
            <div className="p-2 border-b sticky top-0 bg-white z-10">
              <input
                type="text"
                placeholder="Search country..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border rounded outline-none"
              />
            </div>
            <ul ref={dropdownRef} className="max-h-60 overflow-y-auto">
              {filteredList.map(({ code, name, dial_code }) => (
                <li
                  key={code}
                  onClick={() => handleSelect(code)}
                  ref={code === selectedCountry ? selectedItemRef : null}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                >
                  {name} ({dial_code})
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Phone input */}
      <input
        type="text"
        value={localNumber}
        onChange={(e) => onChangeNumber(e.target.value)}
        className="border p-2 rounded-r flex-grow"
        placeholder="e.g., 123456789"
        required
      />
    </div>
  );
}

export default PhoneNumberInput;
